import { asset, onnx } from './runtime';
import { meanings, warm } from './semantics';
import { components } from './imaging';

// Reading the printed part of a challan, in the browser, with nothing sent anywhere.
//
// Two small models do it: one finds where the text is, one reads each line it found. They are the
// mobile PP-OCR models, trained on printed text — which is exactly the half of a challan that is
// printed. On the usual Indian challan book the form and its serial number come off a press and the
// values are written in by hand, so this reads the form and the number, and leaves the handwriting
// to the person at the counter, who is better at it than any model this size.

const DETECT_EDGE = 960; // longest edge the detector sees; the model wants multiples of 32
const INK = 0.3; // a pixel is text when the detector is at least this sure
const MIN_LINE_SCORE = 0.5; // below this a line is treated as unread rather than misread
const REC_HEIGHT = 48; // the height the recogniser was trained at
const REC_MAX_WIDTH = 640;
// A detector of this family is trained to outline a shrunken version of each word, so its boxes are
// deliberately tight and have to be grown again before anything is read out of them. The standard
// expansion - the area over the perimeter, scaled - is the right amount along the line, where too
// little cuts the last character or two off every one of them.
//
// Across the line it is the wrong amount, and wrong in a way that is easy to miss: the crop is
// squeezed to a fixed height before it is read, so padding a line with empty paper above and below
// makes every letter in it smaller. "PPP/0001/25-26" loses a zero that way. So the two directions
// get what each of them needs rather than one number for both.
const UNCLIP = 1.6;
const UNCLIP_ACROSS = 0.15;

const models = {};
const initialize = async () => {
  if (models.ready) return models;
  const ort = await onnx();
  const [detection, recognition, dictionary] = await Promise.all([
    ort.InferenceSession.create(asset('ocr-det.onnx')),
    ort.InferenceSession.create(asset('ocr-rec.onnx')),
    fetch(asset('ocr-dict.txt')).then((r) => r.text()),
  ]);
  // The recogniser's last dimension is its alphabet: a blank for the CTC decoder, then the
  // dictionary, then a space. Reading the width from the model rather than assuming it means a
  // swapped model produces no text instead of confident nonsense.
  const characters = ['', ...dictionary.split('\n').filter((line) => line !== ''), ' '];
  Object.assign(models, {
    ort, detection, recognition, characters, ready: true,
  });
  return models;
};

let loading;
const load = () => {
  if (!loading) loading = initialize().catch((failure) => { loading = null; throw failure; });
  return loading;
};

const context = (canvas) => canvas.getContext('2d', { willReadFrequently: true });

// Pixels to the layout every one of these models wants: channels first, red plane then green then
// blue, each normalised the way its training set was.
const planes = (image, mean, deviation) => {
  const { data, width, height } = image;
  const out = new Float32Array(3 * width * height);
  const area = width * height;
  for (let i = 0; i < area; i += 1) {
    out[i] = (data[i * 4] / 255 - mean[0]) / deviation[0];
    out[area + i] = (data[i * 4 + 1] / 255 - mean[1]) / deviation[1];
    out[area * 2 + i] = (data[i * 4 + 2] / 255 - mean[2]) / deviation[2];
  }
  return out;
};

const detect = async (source) => {
  const { ort, detection } = models;
  const scale = Math.min(1, DETECT_EDGE / Math.max(source.width, source.height));
  const width = Math.max(32, Math.round((source.width * scale) / 32) * 32);
  const height = Math.max(32, Math.round((source.height * scale) / 32) * 32);
  const work = document.createElement('canvas');
  work.width = width; work.height = height;
  context(work).drawImage(source, 0, 0, width, height);
  const input = planes(context(work).getImageData(0, 0, width, height), [0.485, 0.456, 0.406], [0.229, 0.224, 0.225]);
  const result = await detection.run({ [detection.inputNames[0]]: new ort.Tensor('float32', input, [1, 3, height, width]) });
  const map = result[detection.outputNames[0]].data;

  // The probability map becomes a mask, and each connected blob in the mask is a line of text. Every
  // detector of this family works this way; the outline itself is never wanted, only the box round
  // it, which is exactly what the labelling returns.
  const mask = new Uint8Array(map.length);
  for (let i = 0; i < map.length; i += 1) mask[i] = map[i] > INK ? 1 : 0;
  const boxes = [];
  components(mask, width, height).forEach((blob) => {
    const { left, top } = blob;
    const across = blob.right - blob.left + 1; const down = blob.bottom - blob.top + 1;
    if (across < 4 || down < 4) return;
    // How sure the detector was inside this blob, so a faint smudge is dropped before it is handed
    // to the recogniser and comes back as a word.
    let total = 0;
    for (let y = top; y < top + down; y += 1) {
      for (let x = left; x < left + across; x += 1) total += map[y * width + x];
    }
    const score = total / (across * down);
    if (score < INK) return;
    const along = (across * down * UNCLIP) / (2 * (across + down));
    const over = down * UNCLIP_ACROSS;
    boxes.push({
      x: Math.max(0, (left - along) / scale),
      y: Math.max(0, (top - over) / scale),
      width: Math.min(source.width, (across + along * 2) / scale),
      height: Math.min(source.height, (down + over * 2) / scale),
      score,
    });
  });
  // Reading order: down the page, then across. Rows are banded by height because two words on one
  // line are never detected at exactly the same y.
  return boxes.sort((a, b) => (Math.abs(a.y - b.y) > Math.min(a.height, b.height) * 0.6 ? a.y - b.y : a.x - b.x));
};

const recognise = async (source, box) => {
  const { ort, recognition, characters } = models;
  const width = Math.min(REC_MAX_WIDTH, Math.max(16, Math.round((REC_HEIGHT * box.width) / box.height)));
  const work = document.createElement('canvas');
  work.width = width; work.height = REC_HEIGHT;
  context(work).drawImage(source, box.x, box.y, box.width, box.height, 0, 0, width, REC_HEIGHT);
  const input = planes(context(work).getImageData(0, 0, width, REC_HEIGHT), [0.5, 0.5, 0.5], [0.5, 0.5, 0.5]);
  const result = await recognition.run({ [recognition.inputNames[0]]: new ort.Tensor('float32', input, [1, 3, REC_HEIGHT, width]) });
  const output = result[recognition.outputNames[0]];
  const [, steps, alphabet] = output.dims;

  // Greedy CTC: the best character at each step, with blanks and immediate repeats dropped. The
  // confidence is the mean of the winning probabilities, which is what separates a line the model
  // read from one it guessed at.
  let text = '';
  let previous = -1;
  let confidence = 0;
  let counted = 0;
  for (let step = 0; step < steps; step += 1) {
    let best = 0;
    let bestValue = -1;
    for (let c = 0; c < alphabet; c += 1) {
      const value = output.data[step * alphabet + c];
      if (value > bestValue) { bestValue = value; best = c; }
    }
    if (best !== 0 && best !== previous) { text += characters[best] ?? ''; confidence += bestValue; counted += 1; }
    previous = best;
  }
  return { text: text.trim(), score: counted ? confidence / counted : 0 };
};

// What a challan number looks like anywhere in India: letters and digits, usually with a slash or a
// dash, and at least two digits in it. Loose enough for any customer's series, tight enough that a
// printed word is never offered as one.
const SERIAL = /^[A-Z0-9][A-Z0-9/\-_]{3,23}$/i;
const looksLikeSerial = (token) => SERIAL.test(token) && (token.match(/[0-9]/g) || []).length >= 2;

// Standing next to the words "Challan No." changes what counts. Plenty of challan books number
// their pages 1, 2, 3 and the thirty-second challan of the year says "32" — a token that is far too
// short to be recognised as a serial anywhere else on the page, and unmistakable here. So an
// anchored value only has to be short, printable and carry a digit.
const VALUE = /^[A-Z0-9][A-Z0-9/\-_]{0,23}$/i;
const looksLikeValue = (token) => VALUE.test(token) && /[0-9]/.test(token);

// A label names the number; it does not name the document. "DELIVERY CHALLAN" across the top of a
// page says what the paper is, and taking it for a label is how a reader ends up pointing at the
// title and reporting that it found nothing. So a label has to say both what and that a number
// follows: "Challan Number", "Invoice No.", "D.C. No".
const SUBJECT = /(challan|chalan|delivery\s*note|\bd\.?\s*c\.?\b|invoice|\bgrn\b|bill)/i;
const NUMBERISH = /(\bnos?\.?\b|\bnumber\b|#)/i;
// A GST challan carries several numbered documents — its own, an invoice, an e-way bill. The one
// that says challan is the one this yard runs on.
const preference = (text) => (/(challan|chalan)/i.test(text) ? 0 : 1);

// Printed forms separate a label from its value with a colon, a pipe or a run of dots, and none of
// those belong to the number.
const tokensOf = (text) => text
  .split(/[\s|]+/)
  .map((token) => token.replace(/^[:.\-–]+/, '').replace(/[.,;:|]+$/, ''))
  .filter(Boolean);

const valueFor = (label, readable) => {
  // The number printed on the same line as its label, after the label itself.
  const own = tokensOf(label.text.replace(SUBJECT, ' ').replace(NUMBERISH, ' ')).filter(looksLikeValue);
  if (own.length) return { number: own[0], from: 'label', box: label.box };
  // Otherwise the next thing printed to the right of it, on the same row — which is how every
  // two-column form in the country is laid out.
  const middle = label.box.y + label.box.height / 2;
  const beside = readable
    .filter((other) => other !== label
      && other.x > label.box.x + label.box.width * 0.5
      && Math.abs((other.box.y + other.box.height / 2) - middle) < label.box.height * 0.9)
    .sort((a, b) => a.x - b.x)
    .map((other) => tokensOf(other.text).filter(looksLikeValue).map((token) => ({ token, box: other.box })))
    .flat();
  if (beside.length) return { number: beside[0].token, from: 'label', box: beside[0].box };
  return { number: '', from: 'handwritten', box: label.box };
};

/**
 * The challan number this document is most likely to carry, and why.
 *
 * A pre-printed serial beside a printed "Challan No." is the strong case and reads reliably. Where
 * the number is written in by hand the label is still found, and its box comes back so the counter
 * can be shown that part of the page enlarged instead of squinting at the whole sheet.
 */
export const pickChallan = (lines) => {
  const readable = lines.filter((line) => line.score >= MIN_LINE_SCORE && line.text);
  const labels = readable
    .filter((line) => SUBJECT.test(line.text) && NUMBERISH.test(line.text))
    .sort((a, b) => preference(a.text) - preference(b.text));
  const found = labels.map((label) => valueFor(label, readable)).find((result) => result.number);
  if (found) return found;
  if (labels.length) return { number: '', from: 'handwritten', box: labels[0].box };
  const loose = readable
    .map((line) => tokensOf(line.text))
    .flat()
    .filter(looksLikeSerial);
  return loose.length ? { number: loose[0], from: 'guess', box: null } : { number: '', from: 'none', box: null };
};

/**
 * The challan number a printed code carries, where it carries one.
 *
 * A GST e-invoice QR is a signed token whose middle section is plain JSON naming the document it
 * was issued for, so the number is known exactly rather than recognised. A challan may also carry
 * codes that are nothing to do with its number — the payment QR beside the bank details is the
 * usual one — and those are ignored rather than typed into the field.
 */
export const challanFromCode = (payload) => {
  const value = String(payload || '').trim();
  if (!value) return '';
  const parts = value.split('.');
  if (parts.length === 3 && parts[1]) {
    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const body = JSON.parse(window.atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
      const document = body.DocNo || body.docNo || body.data?.DocNo;
      if (document) return String(document).trim().slice(0, 100);
    } catch {
      // Not a token this understands, which is not an error — it is one of the other codes.
    }
  }
  return looksLikeSerial(value) ? value.slice(0, 100) : '';
};

/**
 * The date a vehicle document runs out, read off a photograph of it.
 *
 * Indian paperwork writes dates every way there is — 31/03/2031, 31-03-2031, 31-Mar-2031 — and an
 * RC or a policy carries several of them, of which only one is the expiry. So a labelled date wins
 * outright: "Valid Upto", "Valid Till", "Date of Expiry" are printed on the documents this reads.
 * Failing a label, the latest date on the page is offered as a guess, because an issue date and a
 * tax date are always earlier than the expiry — and it is returned marked as a guess, for a clerk
 * to confirm against the paper rather than accept blind.
 */
const MONTHS = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};
const VALIDITY = /(valid|expir)/i;
const DAY_FIRST = /(\d{1,2})[-/.\s](\d{1,2})[-/.\s](\d{2,4})/;
const DAY_MONTH_NAME = /(\d{1,2})[-/.\s]*([A-Za-z]{3,9})[-/.\s]*(\d{2,4})/;
const ISO_DATE = /(\d{4})-(\d{2})-(\d{2})/;

const iso = (year, month, day) => {
  const full = year < 100 ? 2000 + year : year;
  if (month < 1 || month > 12 || day < 1 || day > 31 || full < 1990 || full > 2100) return '';
  // A calendar check, because 31-02 reads perfectly well and is not a date. Built in UTC on
  // purpose: a date string without a zone is parsed as local time, and asking a local date for its
  // UTC day answers with the day before through half the world, this one included.
  const checked = new Date(Date.UTC(full, month - 1, day));
  if (checked.getUTCFullYear() !== full || checked.getUTCMonth() !== month - 1 || checked.getUTCDate() !== day) return '';
  return `${full}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const datesIn = (text) => {
  const found = [];
  const named = text.match(DAY_MONTH_NAME);
  if (named && MONTHS[named[2].slice(0, 3).toLowerCase()]) {
    const value = iso(Number(named[3]), MONTHS[named[2].slice(0, 3).toLowerCase()], Number(named[1]));
    if (value) found.push(value);
  }
  const plain = text.match(ISO_DATE);
  if (plain) { const value = iso(Number(plain[1]), Number(plain[2]), Number(plain[3])); if (value) found.push(value); }
  const plainDay = text.match(DAY_FIRST);
  // Day first, which is how every Indian document writes it.
  if (plainDay) { const value = iso(Number(plainDay[3]), Number(plainDay[2]), Number(plainDay[1])); if (value) found.push(value); }
  return found;
};

export const pickExpiry = (lines) => {
  const readable = lines.filter((line) => line.score >= MIN_LINE_SCORE && line.text);
  const labelled = readable.filter((line) => VALIDITY.test(line.text));
  const beside = labelled.map((line) => {
    const own = datesIn(line.text);
    if (own.length) return { expiresOn: own[own.length - 1], from: 'label' };
    const middle = line.box.y + line.box.height / 2;
    const next = readable
      .filter((other) => other !== line
        && other.x > line.box.x
        && Math.abs((other.box.y + other.box.height / 2) - middle) < line.box.height * 0.9)
      .sort((a, b) => a.x - b.x)
      .map((other) => datesIn(other.text))
      .flat();
    return next.length ? { expiresOn: next[0], from: 'label' } : null;
  }).find(Boolean);
  if (beside) return beside;
  const all = readable.map((line) => datesIn(line.text)).flat().sort();
  return all.length ? { expiresOn: all[all.length - 1], from: 'guess' } : { expiresOn: '', from: 'none' };
};

// How much of the page is read before the rest of it is. A challan states its number in the header;
// a GST challan also carries an item table, a tax summary and three paragraphs of terms, and
// reading all hundred and fifty lines of that costs half a minute on a phone to learn nothing. So
// the header is read first, and the remainder only when the header did not answer the question.
const HEADER_DEPTH = 0.55;
const HEADER_LINES = 45;
const FURTHER_LINES = 45;

const readLines = async (canvas, boxes, lines, onProgress, offset, total) => {
  for (let i = 0; i < boxes.length; i += 1) {
    onProgress?.({ stage: 'reading', done: offset + i, total });
    // Deliberately one at a time: these run on a single wasm thread, and a phone asked to run
    // twenty at once stops painting the progress the person is watching.
    // eslint-disable-next-line no-await-in-loop
    const { text, score } = await recognise(canvas, boxes[i]);
    if (text) {
      lines.push({
        text, score, box: boxes[i], x: boxes[i].x,
      });
    }
  }
  return lines;
};

/**
 * Every line of printed text this needed to read, in reading order, each with what it says, how
 * sure the model is, and where it sits — the boxes are what let the counter be shown a region of
 * the page rather than told about it.
 */
export const readDocument = async (canvas, onProgress) => {
  await load();
  onProgress?.({ stage: 'detecting' });
  const boxes = await detect(canvas);
  const header = boxes.filter((box) => box.y < canvas.height * HEADER_DEPTH).slice(0, HEADER_LINES);
  const rest = boxes.filter((box) => box.y >= canvas.height * HEADER_DEPTH).slice(0, FURTHER_LINES);
  const lines = await readLines(canvas, header, [], onProgress, 0, header.length);
  return readLines(canvas, rest, lines, onProgress, header.length, header.length + rest.length);
};

export const loadModels = load;

/**
 * Fetches the reader, and then the sentence model, before anybody asks.
 *
 * In that order and one after the other: they share a single-threaded runtime, and loading them at
 * once made the first scan of a cold browser compete with its own preparation.
 */
export const warmScanner = async () => {
  try { await load(); } catch (unavailable) { return; }
  await warm();
};
export const LINE_SCORE_FLOOR = MIN_LINE_SCORE;

// What else a printed challan carries that the intake form is about to ask for.
//
// Forms do not agree on wording. One says "Transporter", the next "Carrier", a third "Fleet Owner
// / Vehicle Provider"; the consignor is variously supplier, sender, shipper, or the mine itself.
// Matching the exact words meant every third challan filled in nothing, so a label is matched by
// what it means: each field carries the vocabulary it is known by, and the label that shares the
// most of that vocabulary wins. OCR mangles letters too, so words are compared by how close they
// are rather than by whether they are identical.
const VOCABULARY = {
  consignmentId: ['consignment', 'order', 'po', 'purchase', 'reference', 'eway', 'ewaybill', 'waybill', 'docket', 'lr'],
  transporter: ['transporter', 'transport', 'carrier', 'fleet', 'owner', 'agency', 'logistics', 'roadways'],
  consignor: ['consignor', 'consigner', 'supplier', 'sender', 'shipper', 'mine', 'despatched', 'dispatched', 'from'],
  material: ['material', 'goods', 'commodity', 'product', 'item', 'description', 'particulars', 'grade'],
  vehicle: ['vehicle', 'truck', 'lorry', 'tanker', 'trailer', 'registration'],
  quantity: ['qty', 'quantity', 'weight', 'net'],
  quantityUnit: ['unit', 'units', 'uom'],
  driver: ['driver', 'driven', 'operator'],
  direction: ['movement', 'direction', 'despatch'],
  challanDate: ['dated', 'issued'],
};
// Words that head a column on a printed challan. They name what is below them, so a line made of
// them is a heading and never a value - "HSN" beside "Description of goods" is the next column,
// not what the truck is carrying.
const HEADERS = new Set(['hsn', 'sac', 'sr', 'srno', 'rate', 'amount', 'tax', 'disc', 'discount',
  'price', 'value', 'total', 'signature', 'remarks']);
// A word carrying no meaning of its own on a form. "Details", "name" and "no" appear beside every
// field there is, so they say nothing about which one this is.
const NOISE = new Set(['details', 'detail', 'name', 'no', 'nos', 'number', 'the', 'of', 'and', 'party', 'address']);
// Below this a match is a coincidence. Two thirds of a word is about one wrong letter in three.
const CLOSE = 0.72;
// To claim a field outright a label has to name it almost exactly. "Delivery" is close enough to
// "delivered" to pass a loose test, and the words across the top of every challan in the country
// are "DELIVERY CHALLAN".
const STRONG = 0.85;
// And below this the label is not about the field at all, however its words scored.
const CONFIDENT = 0.55;

// How alike two words are, 0 to 1, by the edits it takes to turn one into the other. OCR reads
// "Transporler" and "Consigncr"; a reader that insists on the spelling reads nothing.
const likeness = (a, b) => {
  if (a === b) return 1;
  if (Math.abs(a.length - b.length) > 3) return 0;
  const rows = Array.from({ length: b.length + 1 }, (unused, i) => [i, ...Array(a.length).fill(0)]);
  for (let i = 0; i <= a.length; i += 1) rows[0][i] = i;
  for (let down = 1; down <= b.length; down += 1) {
    for (let across = 1; across <= a.length; across += 1) {
      const cost = a[across - 1] === b[down - 1] ? 0 : 1;
      rows[down][across] = Math.min(rows[down - 1][across] + 1, rows[down][across - 1] + 1, rows[down - 1][across - 1] + cost);
    }
  }
  return 1 - rows[b.length][a.length] / Math.max(a.length, b.length);
};

const meaningful = (text) => text
  .toLowerCase()
  .replace(/[^a-z\s]/g, ' ')
  .split(/\s+/)
  .filter((word) => word.length > 1 && !NOISE.has(word));

// How strongly a label speaks for a field: the best word-to-word likeness, averaged over the
// label's own words, so "Transporter" beats "Transporter and Vehicle Details" for the transporter
// and loses to "Vehicle No" for the vehicle.
const speaksFor = (words, vocabulary) => {
  if (!words.length) return 0;
  const scores = words.map((word) => Math.max(...vocabulary.map((term) => likeness(word, term))));
  const best = Math.max(...scores);
  if (best < STRONG) return 0;
  return (best + scores.reduce((sum, score) => sum + score, 0) / scores.length) / 2;
};

// A quantity is a number and a unit together, wherever the form happens to print it.
const QUANTITY = /(\d+(?:\.\d+)?)\s*(mt|kgs?|tons?|tonnes?|qtl|ltr|litres?|nos?|pcs|t)\b/i;
const UNITS = {
  kgs: 'kg', ton: 't', tons: 't', tonne: 't', tonnes: 't', mt: 't', t: 't',
};

// What is printed under a heading, in the column it heads: the nearest line below that shares
// most of the heading's width. This is how every table on every challan is laid out, and reading
// only sideways is why the quantity and the goods never reached the form.
const textBelow = (label, readable) => {
  const left = label.box.x;
  const right = label.box.x + label.box.width;
  const below = readable
    .filter((other) => other !== label && other.box.y > label.box.y + label.box.height * 0.6)
    .filter((other) => {
      const shared = Math.min(right, other.box.x + other.box.width) - Math.max(left, other.box.x);
      return shared > Math.min(label.box.width, other.box.width) * 0.5;
    })
    .sort((a, b) => a.box.y - b.box.y);
  // Only the row immediately under the heading. Further down is another row, or the next table.
  const first = below[0];
  if (!first || first.box.y - (label.box.y + label.box.height) > label.box.height * 3) return '';
  return first.text.replace(/^[:.\-–\s]+/, '').trim();
};

const textBeside = (label, readable) => {
  const middle = label.box.y + label.box.height / 2;
  const beside = readable
    .filter((other) => other !== label
      && other.x > label.box.x + label.box.width * 0.5
      && Math.abs((other.box.y + other.box.height / 2) - middle) < label.box.height * 0.9)
    .sort((a, b) => a.x - b.x)[0];
  return beside ? beside.text.replace(/^[:.\-–\s]+/, '').trim() : '';
};

// A value is what was printed, not what the label said again: "Consignee" beside "Consignor" is
// the next label, not a name. But a haulier is entitled to call itself Shree Balaji Roadways, so a
// line only reads as a label when it is short and nearly all of it is form vocabulary - one
// recognised word inside somebody's name proves nothing.
const isLabel = (text) => {
  const words = meaningful(text);
  if (!words.length || words.length > 4) return false;
  const known = words.filter((word) => HEADERS.has(word) || Object.values(VOCABULARY)
    .some((vocabulary) => vocabulary.some((term) => likeness(word, term) >= CLOSE)));
  return known.length / words.length >= 0.7;
};

// What each line might be a label for. The sentence model decides, because every plant prints its
// own wording and a list of words only covers the forms somebody has already met. Where the model
// cannot be loaded - it is twenty megabytes, and a gate hut's connection is a gate hut's connection
// - the vocabulary below answers instead, which is what this did before the model existed.
const claims = async (readable) => {
  // A label is short. Sentences on a challan are terms and conditions, not field names.
  const heading = (line) => line.text.split(/[:|]/)[0].trim();
  const asking = readable.filter((line) => meaningful(heading(line)).length > 0 && meaningful(heading(line)).length <= 5);
  try {
    // Load semantic matching for the first scan too. If unavailable, use the vocabulary fallback.
    const read = await meanings(asking.map(heading));
    return read
      .map((meaning, index) => (meaning ? { key: meaning.key, line: asking[index], score: meaning.score } : null))
      .filter(Boolean);
  } catch (unavailable) {
    const found = [];
    readable.forEach((line) => {
      const words = meaningful(line.text);
      Object.entries(VOCABULARY).forEach(([key, vocabulary]) => {
        const score = speaksFor(words, vocabulary);
        if (score >= CONFIDENT) found.push({ key, line, score });
      });
    });
    return found;
  }
};

/**
 * The consignment details printed on a challan, as the intake form names them.
 *
 * Each field goes to the label that means it most strongly, and no label fills two fields, so
 * "Transporter Name" and "Transporter Doc No" cannot both claim to be the carrier. Returned only
 * where something was actually printed beside, under or after the label, which leaves the rest to
 * the person holding the paper.
 */
export const readFields = async (lines) => {
  const readable = lines.filter((line) => line.score >= MIN_LINE_SCORE && line.text);
  const candidates = await claims(readable);
  // Standard labels take precedence over an uncertain semantic match. Semantics still handles
  // unfamiliar wording; a model miss must not drop an explicitly named field.
  const labels = {
    consignmentId: /^(?:order|purchase order|consignment)(?:\s+(?:no\.?|number|id))?$/i,
    transporter: /^(?:transporter|carrier|haulier)$/i,
    consignor: /^(?:consignor|supplier|sender)$/i,
    driver: /^(?:driver|driver name)$/i,
    vehicle: /^(?:vehicle|truck|lorry)(?:\s+(?:no\.?|number|registration))?$/i,
    direction: /^(?:movement|movement type|direction)$/i,
    challanDate: /^challan date$/i,
    material: /^(?:description of goods|material|commodity)$/i,
    quantity: /^(?:qty|quantity|net weight)$/i,
    quantityUnit: /^(?:unit|uom|unit of measure)$/i,
  };
  readable.forEach((line) => {
    const heading = line.text.split(/[:|]/)[0].trim();
    Object.entries(labels).forEach(([key, pattern]) => {
      if (pattern.test(heading)) candidates.push({ key, line, score: 1.1 });
    });
  });

  const found = {};
  const claimed = new Set();
  candidates.sort((a, b) => b.score - a.score).forEach(({ key, line }) => {
    if (found[key] || claimed.has(line)) return;
    // Three places a form puts a value: after the label on its own line where the two are actually
    // separated, in the column beside the label, or - in a table - in the cell beneath it.
    const divided = /[:|]/.test(line.text);
    const own = divided ? line.text.replace(/^[^:|]*[:|]/, '').trim() : '';
    const column = ['material', 'quantity', 'quantityUnit'].includes(key) && readable.some((other) => other !== line && /^(?:hsn|sac|qty|quantity|unit|uom)$/i.test(other.text.trim()) && Math.abs(other.box.y - line.box.y) < line.box.height);
    const adjacent = column ? [textBelow(line, readable)] : [textBeside(line, readable), textBelow(line, readable)];
    const value = [own, ...adjacent]
      .find((candidate) => candidate && candidate.length >= (['quantity', 'quantityUnit'].includes(key) ? 1 : 2) && candidate.length <= 100 && !isLabel(candidate));
    if (!value) return;
    found[key] = value.replace(/\s{2,}/g, ' ');
    claimed.add(line);
  });

  // A date is only useful to the form as a date: whatever the paper wrote, it goes in as one, and
  // anything that is not a date on the calendar is dropped rather than typed in wrong.
  if (found.challanDate) {
    const [first] = datesIn(found.challanDate);
    if (first) found.challanDate = first; else delete found.challanDate;
  }

  // A recogniser trained on printed text reads O as 0 and I as 1, which is right far more often
  // than it is wrong: most of a challan's characters are digits. It is wrong in the prefix of a
  // reference, where "SO-2026-15732" is letters by convention and "S0" is not a word. Only that
  // leading run is repaired, and only where it holds a real letter to vouch for it.
  const lettersFirst = (value) => value.replace(/^[A-Z0-9]+(?=[-/\s])/i, (prefix) => (
    /[A-Z]/i.test(prefix) && prefix.length <= 4
      ? prefix.replace(/0/g, 'O').replace(/1/g, 'I').replace(/5/g, 'S')
      : prefix));
  if (found.consignmentId) found.consignmentId = lettersFirst(found.consignmentId);

  // A movement is inbound or outbound, however the form phrased it.
  if (found.direction) {
    const said = found.direction.toLowerCase();
    if (/in(ward|bound|coming)|receipt|delivery to plant/.test(said)) found.direction = 'INBOUND';
    else if (/out(ward|bound|going)|despatch|dispatch|delivery to customer/.test(said)) found.direction = 'OUTBOUND';
    else delete found.direction;
  }
  // A quantity is a number. Whatever the column said, anything else came from the wrong cell.
  if (found.quantity && !/^\d+(\.\d+)?$/.test(found.quantity.replace(/,/g, ''))) delete found.quantity;
  if (found.quantityUnit) {
    const unit = found.quantityUnit.toLowerCase().replace(/[^a-z]/g, '');
    found.quantityUnit = UNITS[unit] || unit;
    if (!found.quantityUnit) delete found.quantityUnit;
  }
  // And where the form printed the two together - "Qty 28 MT" on one line - that reads as well.
  const measured = readable.map((line) => line.text.match(QUANTITY)).find(Boolean);
  if (measured && !found.quantity) {
    const [, amount, unit] = measured;
    found.quantity = amount;
    found.quantityUnit = UNITS[unit.toLowerCase()] || unit.toLowerCase();
  }
  return found;
};

export const pickDocumentReference = (lines) => {
  const readable = lines.filter((line) => line.score >= MIN_LINE_SCORE && line.text);
  const heading = /^(?:policy|permit|certificate|registration|puc|document|reference)(?:\s+(?:reference|number|no\.?))?\s*(?:[:|]|$)/i;
  const label = readable.find((line) => heading.test(line.text.trim()));
  if (!label) return '';
  const own = /[:|]/.test(label.text) ? label.text.replace(/^[^:|]*[:|]/, '').trim() : '';
  const value = own || textBeside(label, readable) || textBelow(label, readable);
  return value && value.length <= 100 && /[0-9]/.test(value) && !isLabel(value) ? value : '';
};
